/**
 * One course, before it is opened.
 *
 * The design has no separate screen for this — 1a's row goes straight into 1b —
 * but a course address (`lms.mauvely.com/course/<slug>`) has to land somewhere,
 * and that somewhere cannot be the player: a link shared in a chat is opened by
 * people who have not been assigned the course, and the honest answer to them
 * is this page saying so rather than a frame that refuses.
 *
 * `canOpen` and `lockedReason` are the server's. This page turns the second
 * into a sentence and does no reasoning of its own about who may open what.
 */

import { useState } from 'react';
import type { CourseWire } from '@wire';
import { learn } from '../api';
import { AssignDialog } from '../components/AssignDialog';
import { CourseMark, DeadlinePill, ProgressLine } from '../components/Bits';
import { ErrorNote, Footnote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { HAIR, meta, minutesLabel, monoPlain, plural, secondsLabel } from '../theme';
import { useLoad } from '../useLoad';

/** Why the button is not there. One sentence each, no reasoning after it. */
function lockedLine(course: CourseWire): string {
  switch (course.lockedReason) {
    case 'not_live':
      return 'This course is not published yet.';
    case 'not_assigned':
      return 'This course has not been assigned to you.';
    case 'bundle_order':
      return 'Finish the course before this one first.';
    default:
      return 'This course cannot be opened.';
  }
}

export default function Course({ id }: { id: string }) {
  const { Badge, Button, Icon } = ds();
  const { navigate } = useRouter();
  const { data, error, loading, reload } = useLoad(
    () => learn.course(id),
    'Could not load this course.',
    id
  );
  const [assigning, setAssigning] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const { course, enrolment, lessons, feedback, bundle } = data;
  const finished = !!enrolment?.completedAt;
  const askFeedback = finished && feedback.enabled && !feedback.submitted && !feedback.skipped;

  return (
    <>
      <PageTitle
        title={course.title}
        meta={meta(
          course.code,
          course.lessonCount === null ? null : plural(course.lessonCount, 'lesson'),
          minutesLabel(course.minutes)
        )}
      >
        {data.canTeach && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setAssigning(true)}>
              Assign
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/teach/courses/${course.id}/report`)}
            >
              Report
            </Button>
          </>
        )}
        {/* The feedback form is a door on this page rather than a panel with a
            sentence in it: the button says what it does and a line above it
            would only say the same thing again. */}
        {askFeedback && (
          <Button variant="secondary" size="sm" onClick={() => navigate(`/course/${course.id}/done`)}>
            Give feedback
          </Button>
        )}
        {data.canOpen && (
          <Button variant="primary" size="md" onClick={() => navigate(`/course/${course.id}/play`)}>
            {finished ? 'Open again' : enrolment ? 'Continue' : 'Start'}
          </Button>
        )}
      </PageTitle>

      <OkNote>{ok}</OkNote>

      <Panel style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <CourseMark colour={course.colour} size={48} />

        <div style={{ display: 'grid', gap: 6, minWidth: 220, flex: 1 }}>
          <span style={monoPlain}>
            {enrolment
              ? meta(
                  `${enrolment.percent}% done`,
                  enrolment.score === null ? null : `score ${enrolment.score}%`,
                  enrolment.seconds > 0 ? secondsLabel(enrolment.seconds) : null
                )
              : 'Not started'}
          </span>
          <ProgressLine percent={enrolment?.percent ?? 0} />
        </div>

        <DeadlinePill badge={data.badge} dueAt={data.dueAt} daysLate={data.daysLate} />
        {data.assignedBy && <span style={monoPlain}>Assigned by {data.assignedBy.name}</span>}
      </Panel>

      {!data.canOpen && (
        <Panel>
          <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-muted)' }}>{lockedLine(data)}</p>
        </Panel>
      )}

      {bundle && (
        <Panel style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={monoPlain}>
            {`Bundle · ${bundle.position + 1} of ${bundle.total}`}
          </span>
          <Link to={`/bundle/${bundle.id}`}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
              {bundle.title}
            </span>
          </Link>
        </Panel>
      )}

      <Panel style={{ display: 'grid', gap: 4 }}>
        <PanelHead title="Lessons" meta={data.version === null ? null : `Version ${data.version}`} />

        {lessons.map((lesson) => (
          <div
            key={lesson.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '11px 4px',
              borderTop: HAIR
            }}
          >
            <Icon
              name={lesson.completedAt ? 'CheckCircle2' : lesson.reachedAt ? 'Play' : 'Circle'}
              size={16}
              color={lesson.completedAt ? 'var(--success-600)' : 'var(--text-faint)'}
            />
            <span style={{ ...monoPlain, width: 24 }}>{lesson.index + 1}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text-strong)' }}>
              {lesson.title}
            </span>
            {lesson.quiz > 0 && <Badge mono>{`${lesson.quiz} q`}</Badge>}
            <span style={monoPlain}>{secondsLabel(lesson.plannedSeconds)}</span>
          </div>
        ))}

        {lessons.length === 0 && <Footnote>Nothing has been uploaded yet.</Footnote>}
      </Panel>

      {assigning && (
        <AssignDialog
          courseId={course.id}
          subject={course.title}
          onClose={() => setAssigning(false)}
          onDone={(result) => {
            setAssigning(false);
            setOk(
              `Assigned to ${plural(result.assigned, 'person', 'people')}.${
                result.alreadyAssigned > 0 ? ` ${result.alreadyAssigned} already had it.` : ''
              }`
            );
            void reload();
          }}
        />
      )}
    </>
  );
}
